import React, { useState, useEffect } from 'react';
import './FacilitiesModal.css';

interface Bedroom {
  id: string;
  location: 'upstairs' | 'downstairs';
  doubleBeds: number;
  singleBeds: number;
  bunksAndCots: number;
  notes: string;
}

interface LivingRoom {
  hasCouch: boolean;
  hasChairs: boolean;
  chairCount: number;
  hasExtraSleeping: boolean;
  notes: string;
}

interface Kitchen {
  hasFridge: boolean;
  fridgeSize: 'standard' | 'apartment' | 'dorm';
  hasTable: boolean;
  chairCount: number;
  hasMicrowave: boolean;
  hasHotplate: boolean;
  hasDishes: boolean;
  hasPotsAndPans: boolean;
  hasDishPan: boolean;
  hasToaster: boolean;
  hasCoffeeMaker: boolean;
  hasKettle: boolean;
  hasSink: boolean;
  otherItems: string;
}

interface Bathroom {
  hasBathroom: boolean;
  hasSink: boolean;
  hasHotWater: boolean;
  hasToilet: boolean;
  hasShower: boolean;
}

interface AccessInfo {
  stepsToEnter: number;
  stepsInside: number;
  hasLoft: boolean;
}

export interface FacilitiesData {
  sleepCapacity: number;
  bedrooms: Bedroom[];
  livingRoom: LivingRoom;
  kitchen: Kitchen;
  bathroom: Bathroom;
  access: AccessInfo;
  additionalNotes: string;
  needsFixing: string;
}

interface FacilitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FacilitiesData) => void;
  initialData?: FacilitiesData;
  placeName: string;
  placeCode: string;
}

const defaultFacilitiesData: FacilitiesData = {
  sleepCapacity: 0,
  bedrooms: [],
  livingRoom: {
    hasCouch: false,
    hasChairs: false,
    chairCount: 0,
    hasExtraSleeping: false,
    notes: ''
  },
  kitchen: {
    hasFridge: false,
    fridgeSize: 'standard',
    hasTable: false,
    chairCount: 0,
    hasMicrowave: false,
    hasHotplate: false,
    hasDishes: false,
    hasPotsAndPans: false,
    hasDishPan: false,
    hasToaster: false,
    hasCoffeeMaker: false,
    hasKettle: false,
    hasSink: false,
    otherItems: ''
  },
  bathroom: {
    hasBathroom: false,
    hasSink: false,
    hasHotWater: false,
    hasToilet: false,
    hasShower: false
  },
  access: {
    stepsToEnter: 0,
    stepsInside: 0,
    hasLoft: false
  },
  additionalNotes: '',
  needsFixing: ''
};

const LOCATION_OPTIONS = [
  { value: 'upstairs', label: 'Upstairs' },
  { value: 'downstairs', label: 'Downstairs' }
];

const FacilitiesModal: React.FC<FacilitiesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  placeName,
  placeCode
}) => {
  const [activeTab, setActiveTab] = useState('beds');
  const [data, setData] = useState<FacilitiesData>(initialData || defaultFacilitiesData);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
    } else {
      setData(defaultFacilitiesData);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleAddBedroom = () => {
    if (data.bedrooms.length >= 5) return;
    
    const newBedroom: Bedroom = {
      id: Date.now().toString(),
      location: 'upstairs',
      doubleBeds: 0,
      singleBeds: 0,
      bunksAndCots: 0,
      notes: ''
    };
    
    setData(prev => ({
      ...prev,
      bedrooms: [...prev.bedrooms, newBedroom]
    }));
  };

  const handleRemoveBedroom = (id: string) => {
    setData(prev => ({
      ...prev,
      bedrooms: prev.bedrooms.filter(b => b.id !== id)
    }));
  };

  const handleBedroomChange = (id: string, field: keyof Bedroom, value: any) => {
    setData(prev => ({
      ...prev,
      bedrooms: prev.bedrooms.map(b => 
        b.id === id ? { ...b, [field]: value } : b
      )
    }));
  };

  const handleSave = () => {
    onSave(data);
    onClose();
  };

  const renderBedsTab = () => (
    <div className="tab-content">
      <div className="capacity-header">
        <label>
          <span>Total Sleep Capacity:</span>
          <input
            type="number"
            min="0"
            value={data.sleepCapacity}
            onChange={(e) => setData(prev => ({ ...prev, sleepCapacity: parseInt(e.target.value) || 0 }))}
            className="capacity-input"
          />
          <span className="capacity-label">people</span>
        </label>
      </div>

      <div className="bedrooms-list">
        {data.bedrooms.map((bedroom, index) => (
          <div key={bedroom.id} className="bedroom-card">
            <div className="bedroom-header">
              <h4>Bedroom {index + 1}</h4>
              <button 
                className="remove-btn"
                onClick={() => handleRemoveBedroom(bedroom.id)}
                title="Remove bedroom"
              >
                ×
              </button>
            </div>
            
            <div className="bedroom-fields">
              <div className="field-row">
                <label>
                  Location:
                  <select
                    value={bedroom.location}
                    onChange={(e) => handleBedroomChange(bedroom.id, 'location', e.target.value)}
                  >
                    {LOCATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              
              <div className="bed-counts">
                <label>
                  Double:
                  <input
                    type="number"
                    min="0"
                    value={bedroom.doubleBeds}
                    onChange={(e) => handleBedroomChange(bedroom.id, 'doubleBeds', parseInt(e.target.value) || 0)}
                  />
                </label>
                <label>
                  Single:
                  <input
                    type="number"
                    min="0"
                    value={bedroom.singleBeds}
                    onChange={(e) => handleBedroomChange(bedroom.id, 'singleBeds', parseInt(e.target.value) || 0)}
                  />
                </label>
                <label>
                  Bunks/Cots:
                  <input
                    type="number"
                    min="0"
                    value={bedroom.bunksAndCots}
                    onChange={(e) => handleBedroomChange(bedroom.id, 'bunksAndCots', parseInt(e.target.value) || 0)}
                  />
                </label>
              </div>
              
              <div className="field-row">
                <label>
                  Notes:
                  <input
                    type="text"
                    value={bedroom.notes}
                    onChange={(e) => handleBedroomChange(bedroom.id, 'notes', e.target.value)}
                    placeholder="e.g., Queen bed, dresser"
                  />
                </label>
              </div>
            </div>
          </div>
        ))}

        {data.bedrooms.length < 5 && (
          <button className="add-bedroom-btn" onClick={handleAddBedroom}>
            + Add Bedroom
          </button>
        )}

        {data.bedrooms.length === 0 && (
          <p className="no-bedrooms">No bedrooms added yet. Click "Add Bedroom" to start.</p>
        )}
      </div>

      <div className="loft-checkbox">
        <label>
          <input
            type="checkbox"
            checked={data.access.hasLoft}
            onChange={(e) => setData(prev => ({
              ...prev,
              access: { ...prev.access, hasLoft: e.target.checked }
            }))}
          />
          Has Loft (ladder access to beds/cots)
        </label>
      </div>
    </div>
  );

  const renderAccessTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h4>Accessibility</h4>
        
        <div className="field-group">
          <label>
            Steps to enter unit:
            <input
              type="number"
              min="0"
              value={data.access.stepsToEnter}
              onChange={(e) => setData(prev => ({
                ...prev,
                access: { ...prev.access, stepsToEnter: parseInt(e.target.value) || 0 }
              }))}
            />
          </label>
        </div>

        <div className="field-group">
          <label>
            Steps inside (if two floors):
            <input
              type="number"
              min="0"
              value={data.access.stepsInside}
              onChange={(e) => setData(prev => ({
                ...prev,
                access: { ...prev.access, stepsInside: parseInt(e.target.value) || 0 }
              }))}
            />
          </label>
        </div>

        <div className="field-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.access.hasLoft}
              onChange={(e) => setData(prev => ({
                ...prev,
                access: { ...prev.access, hasLoft: e.target.checked }
              }))}
            />
            Has loft (ladder access)
          </label>
        </div>
      </div>
    </div>
  );

  const renderLivingTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h4>Living Room</h4>
        
        <div className="checkbox-grid">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.livingRoom.hasCouch}
              onChange={(e) => setData(prev => ({
                ...prev,
                livingRoom: { ...prev.livingRoom, hasCouch: e.target.checked }
              }))}
            />
            Couch
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.livingRoom.hasExtraSleeping}
              onChange={(e) => setData(prev => ({
                ...prev,
                livingRoom: { ...prev.livingRoom, hasExtraSleeping: e.target.checked }
              }))}
            />
            Extra sleeping space
          </label>
        </div>

        <div className="field-group inline">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.livingRoom.hasChairs}
              onChange={(e) => setData(prev => ({
                ...prev,
                livingRoom: { ...prev.livingRoom, hasChairs: e.target.checked }
              }))}
            />
            Chairs (other than kitchen)
          </label>
          {data.livingRoom.hasChairs && (
            <label className="inline-number">
              How many:
              <input
                type="number"
                min="0"
                value={data.livingRoom.chairCount}
                onChange={(e) => setData(prev => ({
                  ...prev,
                  livingRoom: { ...prev.livingRoom, chairCount: parseInt(e.target.value) || 0 }
                }))}
              />
            </label>
          )}
        </div>

        <div className="field-group">
          <label>
            Other notes:
            <textarea
              value={data.livingRoom.notes}
              onChange={(e) => setData(prev => ({
                ...prev,
                livingRoom: { ...prev.livingRoom, notes: e.target.value }
              }))}
              placeholder="Additional living room details..."
            />
          </label>
        </div>
      </div>
    </div>
  );

  const renderKitchenTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h4>Kitchen</h4>
        
        <div className="field-group inline">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasFridge}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasFridge: e.target.checked }
              }))}
            />
            Fridge
          </label>
          {data.kitchen.hasFridge && (
            <select
              value={data.kitchen.fridgeSize}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, fridgeSize: e.target.value as any }
              }))}
            >
              <option value="standard">Standard</option>
              <option value="apartment">Apartment</option>
              <option value="dorm">Dorm</option>
            </select>
          )}
        </div>

        <div className="field-group inline">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasTable}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasTable: e.target.checked }
              }))}
            />
            Table
          </label>
          {data.kitchen.hasTable && (
            <label className="inline-number">
              Chairs:
              <input
                type="number"
                min="0"
                value={data.kitchen.chairCount}
                onChange={(e) => setData(prev => ({
                  ...prev,
                  kitchen: { ...prev.kitchen, chairCount: parseInt(e.target.value) || 0 }
                }))}
              />
            </label>
          )}
        </div>

        <div className="checkbox-grid">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasMicrowave}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasMicrowave: e.target.checked }
              }))}
            />
            Microwave
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasHotplate}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasHotplate: e.target.checked }
              }))}
            />
            Hotplate
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasDishes}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasDishes: e.target.checked }
              }))}
            />
            Dishes
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasPotsAndPans}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasPotsAndPans: e.target.checked }
              }))}
            />
            Pots & Pans
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasDishPan}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasDishPan: e.target.checked }
              }))}
            />
            Dish Pan
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasToaster}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasToaster: e.target.checked }
              }))}
            />
            Toaster
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasCoffeeMaker}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasCoffeeMaker: e.target.checked }
              }))}
            />
            Coffee Maker
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasKettle}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasKettle: e.target.checked }
              }))}
            />
            Kettle
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.kitchen.hasSink}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, hasSink: e.target.checked }
              }))}
            />
            Sink
          </label>
        </div>

        <div className="field-group">
          <label>
            Other items:
            <textarea
              value={data.kitchen.otherItems}
              onChange={(e) => setData(prev => ({
                ...prev,
                kitchen: { ...prev.kitchen, otherItems: e.target.value }
              }))}
              placeholder="Additional kitchen items..."
            />
          </label>
        </div>
      </div>
    </div>
  );

  const renderBathroomTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h4>Bathroom</h4>
        
        <div className="checkbox-grid">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.bathroom.hasBathroom}
              onChange={(e) => setData(prev => ({
                ...prev,
                bathroom: { ...prev.bathroom, hasBathroom: e.target.checked }
              }))}
            />
            Has Bathroom
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.bathroom.hasSink}
              onChange={(e) => setData(prev => ({
                ...prev,
                bathroom: { ...prev.bathroom, hasSink: e.target.checked }
              }))}
            />
            Sink with running water
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.bathroom.hasHotWater}
              onChange={(e) => setData(prev => ({
                ...prev,
                bathroom: { ...prev.bathroom, hasHotWater: e.target.checked }
              }))}
            />
            Hot water
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.bathroom.hasToilet}
              onChange={(e) => setData(prev => ({
                ...prev,
                bathroom: { ...prev.bathroom, hasToilet: e.target.checked }
              }))}
            />
            Toilet
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={data.bathroom.hasShower}
              onChange={(e) => setData(prev => ({
                ...prev,
                bathroom: { ...prev.bathroom, hasShower: e.target.checked }
              }))}
            />
            Shower
          </label>
        </div>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h4>Additional Information</h4>
        
        <div className="field-group">
          <label>
            What else should people know about this place?
            <textarea
              value={data.additionalNotes}
              onChange={(e) => setData(prev => ({ ...prev, additionalNotes: e.target.value }))}
              placeholder="Any other important details..."
              rows={4}
            />
          </label>
        </div>

        <div className="field-group">
          <label>
            Is there anything that might need fixing?
            <textarea
              value={data.needsFixing}
              onChange={(e) => setData(prev => ({ ...prev, needsFixing: e.target.value }))}
              placeholder="Maintenance notes..."
              rows={4}
            />
          </label>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'beds', label: 'Beds', render: renderBedsTab },
    { id: 'access', label: 'Access', render: renderAccessTab },
    { id: 'living', label: 'Living Room', render: renderLivingTab },
    { id: 'kitchen', label: 'Kitchen', render: renderKitchenTab },
    { id: 'bathroom', label: 'Bathroom', render: renderBathroomTab },
    { id: 'notes', label: 'Notes', render: renderNotesTab }
  ];

  return (
    <div className="facilities-modal-overlay" onClick={onClose}>
      <div className="facilities-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Facilities Details - {placeName} ({placeCode})</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="tabs-container">
          <div className="tabs-header">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tabs-content">
            {tabs.find(t => t.id === activeTab)?.render()}
          </div>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesModal;


